import { type FormEvent, useState } from "react";
import type { Client, PortalApi } from "../api.ts";
import { Id } from "../Id.tsx";
import { LocalTime } from "../LocalTime.tsx";
import { useLoad } from "../load.ts";
import { moneyText } from "../money.ts";
import { addressLine, clientLink, clientName, quoteLink, stageOf } from "../quotes.ts";
import { Badge, Button, Field, facts, Input, Notice, PageLayout, Table, Toolbar } from "../ui.tsx";

/**
 * The clients (docs/roadmap.md#admin-portal), for every role: a search by name, phone or email
 * (the data API's `findClients`: a word of a name, a phone or an email beginning with what is
 * typed) → a client with their properties, their current policy and the quotes made for them.
 *
 * Addresses: `#/clients`, `#/clients/search/<text>`, `#/clients/<clientId>`.
 */

const searchLink = (text: string) => `#/clients/search/${encodeURIComponent(text)}`;

export function ClientsPage({ api, path }: { api: PortalApi; path: readonly string[] }) {
  const [first, second] = path;
  if (first === "search") return <ClientSearch key={second ?? ""} api={api} text={second ?? ""} />;
  if (first) return <ClientView key={first} api={api} clientId={first} />;
  return <ClientSearch api={api} text="" />;
}

function ClientSearch({ api, text }: { api: PortalApi; text: string }) {
  const [draft, setDraft] = useState(text);
  const found = useLoad(async () => (text.trim() ? api.findClients(text) : undefined), [api, text]);
  const search = (event: FormEvent) => {
    event.preventDefault();
    if (draft.trim()) window.location.hash = searchLink(draft.trim());
  };
  return (
    <PageLayout title="Clients">
      <Toolbar onSubmit={search} role="search">
        <Field label="Find">
          <Input
            type="search"
            value={draft}
            placeholder="Name, phone or email"
            onChange={(event) => setDraft(event.target.value)}
            className="w-64"
          />
        </Field>
        <Button type="submit" variant="primary">
          Search
        </Button>
        <span className="text-muted text-ui">
          a name word, a phone or an email, from its beginning: "jan" finds Jane
        </span>
      </Toolbar>
      {found.loading && text && <p className="text-muted">Searching…</p>}
      {found.error && <Notice tone="bad">{found.error}</Notice>}
      {found.data && found.data.length === 0 && (
        <p className="text-muted">No client matches "{text}".</p>
      )}
      {found.data && found.data.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th>Client</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Properties</th>
            </tr>
          </thead>
          <tbody>
            {found.data.map(({ client, properties }) => (
              <tr key={client.id}>
                <td>
                  <a href={clientLink(client.id)}>{clientName(client)}</a>
                </td>
                <td>{client.phones?.join(", ") || "—"}</td>
                <td>{client.emails?.join(", ") || "—"}</td>
                <td>{properties.map(addressLine).filter(Boolean).join("; ") || "—"}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </PageLayout>
  );
}

function ClientView({ api, clientId }: { api: PortalApi; clientId: string }) {
  const loaded = useLoad(() => api.client(clientId), [api, clientId]);
  const back = { href: "#/clients", label: "← Clients" };
  if (loaded.loading) {
    return (
      <PageLayout title="Client" back={back}>
        <p className="text-muted">Loading…</p>
      </PageLayout>
    );
  }
  if (!loaded.data) {
    return (
      <PageLayout title="Client" back={back}>
        <Notice tone="bad">{loaded.error ?? "The client was not found."}</Notice>
      </PageLayout>
    );
  }
  const { client, properties, quotes } = loaded.data;
  return (
    <PageLayout title={clientName(client)} back={back}>
      <ClientFacts client={client} />

      <h2>Properties</h2>
      {properties.length === 0 ? (
        <p className="text-muted">No property is recorded for this client.</p>
      ) : (
        <Table compact>
          <thead>
            <tr>
              <th>Address</th>
              <th>Use</th>
              <th>Built</th>
              <th>Current carrier</th>
              <th>Current premium</th>
              <th>Expires</th>
            </tr>
          </thead>
          <tbody>
            {properties.map((property) => (
              <tr key={property.id}>
                <td>{addressLine(property) || "—"}</td>
                <td>{property.occupancy ?? "—"}</td>
                <td>{property.yearBuilt ?? "—"}</td>
                <td>{property.priorCarrier ?? "—"}</td>
                <td>
                  {property.priorPremium !== undefined ? moneyText(property.priorPremium) : "—"}
                </td>
                <td>{property.priorPolicyExpiration ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <h2>Quotes</h2>
      {quotes.length === 0 ? (
        <p className="text-muted">No quote has been made for this client.</p>
      ) : (
        <Table compact>
          <thead>
            <tr>
              <th>Created</th>
              <th>Lines</th>
              <th>Stage</th>
              <th>Status</th>
              <th>Quote</th>
            </tr>
          </thead>
          <tbody>
            {quotes.map((quote) => (
              <tr key={quote.id}>
                <td>
                  <LocalTime iso={quote.createdAt} />
                </td>
                <td>{quote.lines ?? "—"}</td>
                <td>
                  <Badge tone={stageOf(quote) === "final" ? "ok" : "neutral"}>
                    {stageOf(quote)}
                  </Badge>
                </td>
                <td>{quote.status ?? "—"}</td>
                <td>
                  <Id id={quote.id} href={quoteLink(quote.id)} />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </PageLayout>
  );
}

function ClientFacts({ client }: { client: Client }) {
  return (
    <dl className={facts}>
      <dt>Kind</dt>
      <dd>{client.kind ?? "—"}</dd>
      {client.contactName && (
        <>
          <dt>Contact</dt>
          <dd>{client.contactName}</dd>
        </>
      )}
      <dt>Phone</dt>
      <dd>{client.phones?.join(", ") || "—"}</dd>
      <dt>Email</dt>
      <dd>{client.emails?.join(", ") || "—"}</dd>
    </dl>
  );
}
